import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { LocalLoadSearchCriteria } from '../../shared/local-load-search-criteria.model';
import { SearchObjectResult } from '../../../shared/search-object-result.model';
import { LocalLoad, LocalLoadStatus } from '../../shared/local-load.model';
import { Observable, Subscription } from 'rxjs';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../shared/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { LocalLoadService } from '../../shared/local-load.service';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants';
import { DataFiltersComponent } from '../../../shared/data-filters/data-filters.component';
import { ReorderLoadsService } from '../../shared/reorder-loads.service';
import { finalize, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Option } from '../../../shared/data-filters/data-filter-autocomplete/data-filter-autocomplete.component';

@Component({
  selector: 'app-search-loads-list',
  templateUrl: './search-loads-list.component.html',
  styleUrls: ['./search-loads-list.component.scss']
})
export class SearchLoadsListComponent implements OnInit, OnDestroy, OnChanges {

  subscription = new Subscription();

  selectedVehicle: string;

  @ViewChild('dataFilters', { static : true })
  dataFilters: DataFiltersComponent;

  carrierSelectionAvailable = false;
  carriers: Carrier[]       = [];

  @Input()
  selectedCarrierId: string;

  filtersBase: LocalLoadSearchCriteria = {
    loadNumbers: [],
    shipment_number: [],
    status: [LocalLoadStatus.PLANNED],
    showUnassigned: false
  };

  searchResult: SearchObjectResult<LocalLoad>;

  filters: LocalLoadSearchCriteria = { ...this.filtersBase };

  lastSearchFilters: LocalLoadSearchCriteria = { ...this.filters };

  loadPageForLoad: (page: number, pageSize: number) => Observable<SearchObjectResult<LocalLoad>> = null;
  searchVehicles: () => Observable<string[]>                                                     = null;
  assignLoadsToVehicle: (loadUuids: any, vehicle: string) => Observable<void>                    = null;

  vehicleSearchOptions: Option[] = [];
  startDate: moment.Moment;
  endDate: moment.Moment;

  constructor(
    private authService: AuthenticationService,
    private loader: LoaderService,
    private translate: TranslateService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private localLoadsService: LocalLoadService,
    private reorderService: ReorderLoadsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    const localStorageFilters: LocalLoadSearchCriteria = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_SEARCH));

    if (localStorageFilters) {
      this.applyFilters(localStorageFilters);
    }

    if (this.authService.canManageCarriers()) {
      this.carrierSelectionAvailable = true;
      this.selectedCarrierId         = localStorage.getItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_CARRIER);


      this.loadPageForLoad      = (page: number, pageSize: number) =>
        this.localLoadsService.searchForCarrier(page - 1, pageSize, this.selectedCarrierId, this.filters);
      this.searchVehicles       = () => this.localLoadsService.getVehiclesForCarrier(this.selectedCarrierId);
      this.assignLoadsToVehicle = (loadUuids, vehicle) =>
        this.localLoadsService.assignLoadsToVehicleForCarrier(this.selectedCarrierId, loadUuids, vehicle)

    } else {
      this.loadPageForLoad      = (page: number, pageSize: number) =>
        this.localLoadsService.searchForSelfCarrier(page - 1, pageSize, this.filters);
      this.searchVehicles       = () => this.localLoadsService.getVehiclesForSelfCarrier();
      this.assignLoadsToVehicle = (loadUuids, vehicle) => this.localLoadsService.assignLoadsToVehicleForSelfCarrier(loadUuids, vehicle)
    }
  }

  ngOnInit() {
    this.subscription.add(this.reorderService.chosenVehicleEvent.subscribe((vehicle) => {
      this.selectedVehicle = vehicle;
    }));

    this.subscription.add(this.reorderService.unassignEvent.subscribe(() => {
      this.dataFilters.apply();
    }))

    this.route.queryParams
      .pipe(take(1))
      .subscribe(params => {
        let vehiclesList    = [];
        let shipmentNumberList = [];

        if (Array.isArray(params.vehicles)) {
          vehiclesList = params.vehicles;
        } else if (params.vehicles !== undefined) {
          vehiclesList.push(params.vehicles);
        }

        if (Array.isArray(params.shipment_number)) {
          shipmentNumberList = params.shipment_number;
        } else if (params.shipment_number !== undefined) {
          shipmentNumberList.push(params.shipment_number);
        }

        this.filters.vehicles       = vehiclesList;
        this.filters.shipment_number    = shipmentNumberList;
        this.filters.updated_start_date = params.updated_start_date ? +params.updated_start_date : null;
        this.filters.updated_end_date = params.updated_end_date ? +params.updated_end_date : null;
        this.filters.showUnassigned = params.showUnassigned;
      });
  }

  updateStartDateFilter() {
    if (this.startDate) {
      this.filters.updated_start_date = this.startDate.utc(true).valueOf();
    } else {
      this.filters.updated_start_date = null;
    }
  }

  updateEndDateFilter() {
    if (this.endDate) {
      this.filters.updated_end_date = this.endDate.utc(true).valueOf();
    } else {
      this.filters.updated_end_date = null;
    }
  }
  applyFilters(filters: LocalLoadSearchCriteria) {
    if (!filters.status) {
      filters.status = [];
    }
    this.filters           = filters;
    this.lastSearchFilters = { ...this.filters };

    if (filters.updated_start_date) {
      this.startDate = moment(filters.updated_start_date);
    }

    if (filters.updated_end_date) {
      this.endDate = moment(filters.updated_end_date);
    }
  }

  pageChanged() {
    this.lastSearchFilters = { ...this.filters };

    localStorage.setItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_SEARCH, JSON.stringify(this.lastSearchFilters));

    // Reapply startDate and endDate to keep the range even if the carrier is changed
    this.updateStartDateFilter();
    this.updateEndDateFilter();

    if (this.carrierSelectionAvailable) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_CARRIER, this.selectedCarrierId);
    }
  }

  onLoad(searchResult: SearchObjectResult<LocalLoad>) {
    this.searchResult = searchResult;

    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: this.filters
      }
    );

    this.loader.startLoad();
    this.searchVehicles()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(vehicles => {
        this.vehicleSearchOptions = vehicles.map(vehicle => ({ value: vehicle, label: vehicle }));
      });
  }

  assignAllToVehicle(content, loads: LocalLoad[]) {
    event.stopPropagation();
    this.modalService.open({
      title: this.translate.instant('local-loads.reorder.modal.assign.title'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.reorder.modal.assign.confirm')
      },
      closeButton: {
        text: this.translate.instant('local-loads.reorder.modal.assign.close')
      },
      size: 'sm'
    }).subscribe(() => {
      this.loader.startLoad();

      this.assignLoadsToVehicle(loads.map(item => item.loadUuid), this.selectedVehicle)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(() => {
          this.refreshLoads();
          this.onLoad(this.searchResult);
          this.toastr.success(this.translate.instant('local-loads.update.success.assign-vehicle', { vehicle: this.selectedVehicle }));
        }, err => {
          console.log(err);
          this.toastr.error(this.translate.instant('local-loads.update.errors.assign-vehicle'));
        });
    });
  }

  refreshLoads() {
    this.dataFilters.apply();
    this.reorderService.assignLoad();
  }

  assignLoad(load: LocalLoad) {
    event.stopPropagation();
    this.loader.startLoad();

    this.assignLoadsToVehicle([load.loadUuid], this.selectedVehicle)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.refreshLoads();
        this.toastr.success(this.translate.instant('local-loads.update.success.assign-vehicle', { vehicle: this.selectedVehicle }));
      }, err => {
        console.log(err);
        this.toastr.error(this.translate.instant('local-loads.update.errors.assign-vehicle'));
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataFilters.reset();
    this.dataFilters.apply();
  }
}
